import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@services/api';
import { settingsService, TenantSettingsResponse } from '@services/settings.service';
import { usePermissions } from '@hooks/usePermissions';
import { resolveApiErrorMessage } from '@utils/apiErrorMessages';
import DataManagementCard, {
  MetadataInfo,
} from '@components/settings/admin/DataManagementCard';
import FeatureSettingsCard from '@components/settings/admin/FeatureSettingsCard';
import { fetchVerificationStatus } from '@features/featureFlags/featureFlagsSlice';
import { showNotification } from '@features/ui/uiSlice';
import { useAppDispatch } from '@store/index';
import ResetConfirmDialog from '@components/settings/admin/ResetConfirmDialog';
import SearchSettingsCard from '@components/settings/admin/SearchSettingsCard';

/** Held unresolved so the copy is chosen at render, in the current language. */
type SettingsError = { error: unknown; fallbackKey: string };

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [metadataInfo, setMetadataInfo] = useState<MetadataInfo | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [resultMessage, setResultMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  // Tenant settings state
  const [tenantSettings, setTenantSettings] = useState<TenantSettingsResponse | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState<SettingsError | null>(null);

  // Feature-flag card keeps its own error banner — sharing the search card's
  // would show one card's failure on both. Success is a toast, so it needs none.
  const [featureError, setFeatureError] = useState<SettingsError | null>(null);

  // Permissions
  const { canManageSettings } = usePermissions();
  const dispatch = useAppDispatch();

  const fetchMetadataInfo = useCallback(async () => {
    try {
      // Raw axios here bypassed the client's auth interceptor, so this request
      // went out unauthenticated and failed on every load.
      setMetadataInfo(await api.get<MetadataInfo>('/admin/metadata/info'));
    } catch (error) {
      console.error('Failed to fetch metadata info:', error);
    }
  }, []);

  const fetchTenantSettings = useCallback(async () => {
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const settings = await settingsService.getTenantSettings();
      setTenantSettings(settings);
    } catch (error) {
      console.error('Failed to fetch tenant settings:', error);
      // Resolved at render; keeping t() out of this callback keeps a language
      // switch from refetching everything.
      setSettingsError({ error, fallbackKey: 'settings.tenantSettingsFetchFailed' });
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  // Fetch metadata info and tenant settings on component mount
  useEffect(() => {
    fetchMetadataInfo();
    fetchTenantSettings();
  }, [fetchMetadataInfo, fetchTenantSettings]);

  const handleKeywordMatchingToggle = async (newValue: boolean) => {
    setSettingsLoading(true);
    setSettingsError(null);

    try {
      const updatedSettings = await settingsService.updateSearchSettings({
        keyword_matching_enabled: newValue,
      });
      setTenantSettings(updatedSettings);
      // The notification queue owns the toast's lifetime, so no local timer.
      dispatch(showNotification({
        type: 'success',
        message: newValue
          ? t('settings.keywordMatchingEnabled')
          : t('settings.keywordMatchingDisabled'),
      }));
    } catch (error) {
      console.error('Failed to update keyword matching setting:', error);
      setSettingsError({ error, fallbackKey: 'settings.updateFailed' });
      // Revert UI state by re-fetching
      await fetchTenantSettings();
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleDesignVerificationToggle = async (newValue: boolean) => {
    setSettingsLoading(true);
    setFeatureError(null);

    try {
      // Only the toggled key is sent; the backend merges field by field, so the
      // sibling flags keep whatever is stored rather than being re-asserted.
      const updatedSettings = await settingsService.updateFeatureSettings({
        design_verification: newValue,
      });
      setTenantSettings(updatedSettings);
      // The store loads this flag only at sign-in, so without it the admin who
      // just flipped the toggle keeps the old navigation until a reload.
      dispatch(fetchVerificationStatus());
      dispatch(showNotification({
        type: 'success',
        message: newValue
          ? t('settings.designVerificationEnabled')
          : t('settings.designVerificationDisabled'),
      }));
    } catch (error) {
      console.error('Failed to update design verification setting:', error);
      setFeatureError({ error, fallbackKey: 'settings.updateFailed' });
      // Revert UI state by re-fetching
      await fetchTenantSettings();
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleResetClick = () => {
    setConfirmDialogOpen(true);
    setResultMessage(null);
  };

  const handleConfirmReset = async () => {
    setConfirmDialogOpen(false);
    setLoading(true);
    setResultMessage(null);

    try {
      const response = await api.post<{ success: boolean; document_count: number }>(
        '/admin/metadata/reset',
      );

      if (response.success) {
        setResultMessage({
          type: 'success',
          text: t('settings.resetComplete', { count: response.document_count }),
        });
        // Refresh metadata info
        await fetchMetadataInfo();
      } else {
        setResultMessage({
          type: 'error',
          text: t('settings.resetFailed'),
        });
      }
    } catch (error) {
      console.error('Reset error:', error);
      setResultMessage({
        type: 'error',
        text: t('settings.resetError', {
          message: resolveApiErrorMessage(t, error, { fallbackKey: 'settings.resetFailed' }),
        }),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReset = () => {
    setConfirmDialogOpen(false);
  };

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <header>
        <h1 className="revy-cjk text-2xl font-semibold leading-[1.4] tracking-tight">
          {t('settings.systemTitle')}
        </h1>
        <p className="revy-cjk mt-1 text-sm leading-[1.6] text-muted-foreground">
          {t('settings.systemDescription')}
        </p>
      </header>

      <SearchSettingsCard
        settings={tenantSettings}
        loading={settingsLoading}
        error={
          settingsError
            ? resolveApiErrorMessage(t, settingsError.error, {
                fallbackKey: settingsError.fallbackKey,
              })
            : null
        }
        canManage={canManageSettings}
        onToggleKeywordMatching={handleKeywordMatchingToggle}
      />

      {/* Design Verification is a customer-specific build, so only the tenant
          it was built for is offered the switch (#1214). The server decides;
          the API refuses the change for anyone else regardless of this. */}
      {tenantSettings?.manageable_features?.includes('design_verification') && (
      <FeatureSettingsCard
        settings={tenantSettings}
        loading={settingsLoading}
        error={
          featureError
            ? resolveApiErrorMessage(t, featureError.error, {
                fallbackKey: featureError.fallbackKey,
              })
            : null
        }
        canManage={canManageSettings}
        onToggleDesignVerification={handleDesignVerificationToggle}
      />
      )}

      <DataManagementCard
        metadataInfo={metadataInfo}
        resetting={loading}
        result={resultMessage}
        onResetClick={handleResetClick}
      />

      <ResetConfirmDialog
        open={confirmDialogOpen}
        onCancel={handleCancelReset}
        onConfirm={handleConfirmReset}
      />
    </div>
  );
};

export default SettingsPage;
